import { useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotebookPen, Plus } from 'lucide-react';
import { CarteNote, ResteCaracteres, TEXTE_NOTE_MAX } from './CarteNote';
import { Chargement, MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { tempsLisible } from '../../lib/temps-video';
import { useAjouterNote, useNotesLecon, type VueLecon } from '../../lib/useLecteur';

/**
 * Onglet « Mes notes » du lecteur (TF-446), sur le modèle de la maquette :
 * les notes de l'apprenant sur la leçon, dans l'ordre de la vidéo, puis la
 * zone de saisie. La note est rattachée au moment où il commence à écrire —
 * la vidéo continue — ; un clic sur ce moment y renvoie la vidéo. Les notes
 * sont privées : l'aperçu de l'auteur et de la direction n'en prend pas, et
 * l'API dit ce qui empêche d'en ajouter.
 */
export function NotesLecon({
  vue,
  temps,
  allerA,
}: {
  vue: VueLecon;
  /** Position de lecture, en secondes. */
  temps: number;
  /** Envoie la vidéo au passage ; `null` sans vidéo. */
  allerA: ((secondes: number) => void) | null;
}): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading, error } = useNotesLecon(vue.lecon.id, true);
  const ajouter = useAjouterNote(vue.lecon.id);
  const [texte, setTexte] = useState('');
  /** Moment retenu à la première frappe ; `null` tant que la note n'est pas commencée. */
  const [moment, setMoment] = useState<number | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const saisie = useRef<HTMLTextAreaElement>(null);

  if (isLoading) return <Chargement libelle="Chargement de vos notes…" />;
  if (error || !data) {
    return <MessageErreur message={error instanceof ErreurApi ? error.message : 'Vos notes n’ont pas pu être chargées.'} />;
  }

  const momentCourant = Math.floor(temps);
  const enregistrer = async () => {
    if (!texte.trim()) {
      setMoment((actuel) => actuel ?? momentCourant);
      saisie.current?.focus();
      return;
    }
    const retenu = moment ?? momentCourant;
    setErreur(null);
    try {
      const note = await ajouter.mutateAsync({ momentSecondes: retenu, texte });
      setTexte('');
      setMoment(null);
      toast(`Note ajoutée à ${tempsLisible(note.momentSecondes)}`);
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'La note n’a pas pu être ajoutée.');
    }
  };

  const clavier = (evenement: KeyboardEvent<HTMLTextAreaElement>) => {
    if (evenement.key === 'Enter' && (evenement.ctrlKey || evenement.metaKey)) {
      evenement.preventDefault();
      void enregistrer();
    }
  };

  const ouverte = data.refusAjout === null;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-xl font-bold text-codialis-navy">Mes notes personnelles</h3>
        {ouverte && (
          <button
            type="button"
            onClick={() => void enregistrer()}
            disabled={ajouter.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-codialis-mint px-4 py-2 text-sm font-semibold text-codialis-navy transition hover:bg-codialis-teal disabled:cursor-not-allowed disabled:opacity-60 [@media(pointer:coarse)]:min-h-[44px]"
          >
            <Plus className="h-4 w-4" aria-hidden />
            {ajouter.isPending ? 'Ajout…' : 'Ajouter'}
          </button>
        )}
      </div>

      {data.notes.length > 0 ? (
        <ol aria-label="Mes notes sur la leçon" className="scrollbar-thin mb-4 max-h-96 space-y-3 overflow-y-auto pr-1">
          {data.notes.map((note) => (
            <CarteNote
              key={note.id}
              note={note}
              contexte={`Module ${vue.lecon.numero.split('.')[0]}`}
              allerA={allerA ? () => allerA(note.momentSecondes) : null}
            />
          ))}
        </ol>
      ) : (
        ouverte && <p className="mb-4 text-sm text-codialis-gray">Aucune note sur cette leçon pour le moment.</p>
      )}

      {ouverte ? (
        <div className="space-y-1.5">
          {moment !== null && (
            <p className="flex flex-wrap items-center gap-x-2 text-xs text-codialis-gray">
              <span>
                Note à <strong className="text-codialis-navy">{tempsLisible(moment)}</strong>
              </span>
              {momentCourant !== moment && (
                <button
                  type="button"
                  onClick={() => setMoment(momentCourant)}
                  className="font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[40px]"
                >
                  Reprendre à {tempsLisible(momentCourant)}
                </button>
              )}
            </p>
          )}
          <textarea
            ref={saisie}
            value={texte}
            rows={3}
            maxLength={TEXTE_NOTE_MAX}
            onChange={(evenement) => {
              const valeur = evenement.target.value;
              setTexte(valeur);
              if (!valeur.trim()) setMoment(null);
              else setMoment((actuel) => actuel ?? momentCourant);
            }}
            onKeyDown={clavier}
            placeholder={`Prendre une note à ${tempsLisible(moment ?? momentCourant)}…`}
            aria-label="Nouvelle note"
            className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-[11px] text-codialis-gray">
              La note est rattachée au moment où vous commencez à l’écrire ; elle n’est lue que par vous.
            </span>
            <ResteCaracteres texte={texte} />
          </div>
          {erreur && <MessageErreur message={erreur} />}
        </div>
      ) : (
        <div className="rounded-2xl bg-gray-50 p-4 text-sm leading-relaxed text-codialis-gray sm:p-5">
          <p className="flex items-start gap-2">
            <NotebookPen className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {data.refusAjout}
          </p>
        </div>
      )}

      {vue.acces === 'APPRENANT' && (
        <button
          type="button"
          onClick={() => navigate('/player?onglet=notes')}
          className="mt-4 text-sm font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]"
        >
          Toutes mes notes
        </button>
      )}
    </div>
  );
}
